// Oppie fullscreen video — <oppie-fullscreen-video data-video-url="…">
//
// Wraps a muted, looping inline video (snippets/oppie-media.liquid). Tapping the
// media opens the screen-filling player WITH sound: a tap is a user gesture, so
// the overlay video may play unmuted. The inline video pauses meanwhile.
//
// The overlay markup lives in sections/oppie-about-row.liquid, not here — that
// keeps its classes visible to the live CSS subset. On open it moves to <body>,
// because the media frame clips overflow and transforms on hover, which would
// break position: fixed. On close it moves back.
//
// The pause/play control from oppie-media.js keeps working: clicks inside it are
// ignored here, so users can still stop the silent loop without opening anything.
// Without JS the inline video simply stays silent — no dead click targets.

const OPEN_ATTR = '[data-oppie-fsv-open]';
const CLOSE_ATTR = '[data-oppie-fsv-close]';
const OVERLAY_ATTR = '[data-oppie-fsv-overlay]';
const TOGGLE_ATTR = '[data-oppie-video-toggle]';
const FOCUSABLE = 'button:not([tabindex="-1"]), [href], video[controls]';

class OppieFullscreenVideo extends HTMLElement {
  connectedCallback() {
    this.url = this.dataset.videoUrl;
    this.overlay = this.querySelector(OVERLAY_ATTR);
    this.player = this.overlay ? this.overlay.querySelector('.oppie-fsv__video') : null;

    if (!this.url || !this.overlay || !this.player) {
      // No video yet: drop the affordance so nothing looks clickable.
      this.querySelectorAll(OPEN_ATTR).forEach((el) => el.remove());
      return;
    }

    this.addEventListener('click', this.#onClick);
    this.overlay.querySelectorAll(CLOSE_ATTR).forEach((el) => el.addEventListener('click', this.#close));
    this.overlay.addEventListener('keydown', this.#onKeydown);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.#close();
  }

  #onClick = (event) => {
    // Let the silent-loop pause button do its own job.
    if (event.target.closest(TOGGLE_ATTR)) return;
    // Clicks inside the open overlay are handled by its own listeners.
    if (event.target.closest(OVERLAY_ATTR)) return;
    event.preventDefault();
    this.#open();
  };

  #inlineVideo() {
    return this.querySelector('.oppie-media__video');
  }

  // De Shopify-chatlauncher (<shopify-chat>) zit op z-index 2147483000 en zou
  // dus over de speler heen zweven. Inline stijl in plaats van een CSS-regel:
  // het element staat nergens in de theme-markup, dus de live CSS-subset zou
  // een regel ervoor wegstrippen.
  #setChatHidden(hidden) {
    const chat = document.querySelector('shopify-chat, #shopify-chat');
    if (!chat) return;
    if (hidden) {
      this.chatDisplay = chat.style.display;
      chat.style.setProperty('display', 'none', 'important');
    } else {
      chat.style.removeProperty('display');
      if (this.chatDisplay) chat.style.display = this.chatDisplay;
    }
  }

  #open() {
    if (this.isOpen) return;
    this.isOpen = true;

    const inline = this.#inlineVideo();
    if (inline) inline.pause();

    this.previousFocus = document.activeElement;
    this.scrollLock = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    this.#setChatHidden(true);
    document.body.appendChild(this.overlay);
    this.overlay.hidden = false;

    this.player.src = this.url;
    this.player.muted = false;
    this.player.volume = 1;
    const played = this.player.play();
    // Some browsers still refuse unmuted playback. Fall back to a silent start
    // rather than a frozen frame; the native controls can unmute from there.
    if (played && typeof played.catch === 'function') {
      played.catch(() => {
        this.player.muted = true;
        this.player.play().catch(() => {});
      });
    }

    this.overlay.querySelector('.oppie-fsv__close').focus();
  }

  #onKeydown = (event) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.#close();
      return;
    }
    if (event.key !== 'Tab') return;
    const items = [...this.overlay.querySelectorAll(FOCUSABLE)];
    if (items.length === 0) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  #close = () => {
    if (!this.isOpen) return;
    this.isOpen = false;

    this.player.pause();
    // Laat de bron los zodat een gesloten speler niet doorbuffert. Geen load()
    // erachteraan: die logt in Chrome een "Empty src"-fout.
    this.player.removeAttribute('src');
    this.overlay.hidden = true;
    this.appendChild(this.overlay);

    document.body.style.overflow = this.scrollLock || '';
    this.#setChatHidden(false);
    if (this.previousFocus && typeof this.previousFocus.focus === 'function') {
      this.previousFocus.focus();
    }
  };
}

if (!customElements.get('oppie-fullscreen-video')) {
  customElements.define('oppie-fullscreen-video', OppieFullscreenVideo);
}
